import { db } from "../firebaseClient"
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore"
import { detectCountryCode } from "./geoHelpers"

// Keeps learnerProfiles/{uid} â€” the public-read doc backing /id/:nesolearnId
// and the future 3D ID card â€” in sync with the freshest name/ID/join-date.
// Self-heal pattern, same spirit as users/{uid}.createdAt in firebaseClient.js:
// call this from anywhere the learner is authenticated and viewing their own
// data, and it fills in gaps without needing a migration. Never overwrites
// `credentials[]`, which only certHelpers.js's issueCertificate() touches.
export const ensurePublicProfile = async ({
  uid,
  name,
  nesolearnId,
  createdAt,
  location,
  coursesCompleted,
  currentStreak,
}) => {
  if (!uid) return { error: { message: "Missing uid" } }

  try {
    const payload = { uid, updatedAt: serverTimestamp() }
    if (name) payload.name = name
    if (nesolearnId) payload.nesolearnId = nesolearnId
    if (createdAt) payload.createdAt = createdAt
    if (location) payload.location = location
    if (typeof coursesCompleted === "number") payload.coursesCompleted = coursesCompleted
    if (typeof currentStreak === "number") payload.currentStreak = currentStreak

    await setDoc(doc(db, "learnerProfiles", uid), payload, { merge: true })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

// Public lookup for /id/:nesolearnId â€” no auth required, mirrors
// certHelpers.js's getCertificate(). nesolearnIds is keyed by the ID itself,
// so this is two getDocs, no query.
export const getPublicProfileByNesolearnId = async (nesolearnId) => {
  try {
    const idSnap = await getDoc(doc(db, "nesolearnIds", nesolearnId))
    if (!idSnap.exists()) return { profile: null, error: null }

    const idData = idSnap.data()
    if (idData?.accountType === "organization") {
      return { profile: null, error: { message: "This ID belongs to an organization, not a learner." } }
    }

    const profileSnap = await getDoc(doc(db, "learnerProfiles", idData.uid))
    const profileData = profileSnap.exists() ? profileSnap.data() : {}

    return {
      profile: {
        uid: idData.uid,
        nesolearnId,
        name: profileData?.name || "Nesolearn Learner",
        createdAt: profileData?.createdAt || null,
        location: profileData?.location || null,
        credentials: profileData?.credentials || [],
        coursesCompleted: typeof profileData?.coursesCompleted === "number" ? profileData.coursesCompleted : null,
        currentStreak: typeof profileData?.currentStreak === "number" ? profileData.currentStreak : null,
      },
      error: null,
    }
  } catch (error) {
    return { profile: null, error }
  }
}

// Detects and stores a learner's country exactly once — self-healing like
// ensurePublicProfile. Safe to call on every login: it no-ops instantly if
// a country is already on file, so it's not repeatedly hitting the geo API.
export const ensureLearnerCountry = async (uid, existingCountry) => {
  if (!uid || existingCountry) return { error: null }
  const code = await detectCountryCode()
  if (!code) return { error: null }
  try {
    await setDoc(doc(db, "learnerProfiles", uid), { country: code, updatedAt: serverTimestamp() }, { merge: true })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

// Batched country lookup for a list of learner userIds — feeds
// getLearnersByCountry. A learner with no detected country yet is simply
// excluded from the breakdown rather than shown as "Unknown".
export const getCountriesForUsers = async (uids) => {
  const countryByUid = {}
  await Promise.all(
    (uids || []).map(async (uid) => {
      try {
        const snap = await getDoc(doc(db, "learnerProfiles", uid))
        const country = snap.exists() ? snap.data()?.country : null
        if (country) countryByUid[uid] = country
      } catch {
        // skip — one bad lookup shouldn't break the whole aggregation
      }
    })
  )
  return countryByUid
}